import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { FileBarChart, Loader2 } from "lucide-react";
import { PageShell } from "../../components/layout/PageShell";
import { postPagedSearchAbsolute } from "../../../api/frameworksApi";
import { REPORTS_URL } from "../../../constants";

interface ReportRow {
  sspId: string;
}

/**
 * Landing route for the system owner report page. Resolves the most recently
 * updated report and redirects straight into its streaming detail view
 * (/system-owner-report/:sspId) so navigating here actually triggers the SSE
 * call instead of sitting on a static placeholder.
 */
export function SystemOwnerReport() {
  const navigate = useNavigate();
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    postPagedSearchAbsolute<ReportRow>(REPORTS_URL.dashboard, 0, 1, {
      keyword: "",
      sortByColumn: "updatedAt",
      sortingOrder: "desc",
    })
      .then((res) => {
        if (cancelled) return;
        const sspId = res.items[0]?.sspId;
        if (sspId) navigate(`/system-owner-report/${sspId}`, { replace: true });
        else setNotFound(true);
      })
      .catch(() => {
        if (!cancelled) setNotFound(true);
      });
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  if (notFound) {
    return (
      <PageShell className="space-y-6">
        <div className="rounded-xl border border-border bg-card p-12 text-center">
          <FileBarChart className="size-8 d mx-auto mb-3" />
          <p className="text-sm font-medium">No reports available yet</p>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell className="flex items-center justify-center py-32">
      <Loader2 className="size-6 animate-spin text-indigo-500" />
      <span className="ml-2 text-sm d">Finding a report...</span>
    </PageShell>
  );
}
